// AddEventModalShell — Presentational outer chrome for the AddEventForm: the
// fixed full-screen overlay/backdrop, the centered white modal container, and
// the header row (the "Add {label}" title with its Plus icon and the close "X"
// button). The modal body — the per-type field group and the footer action bar
// — is supplied via {children} so the container (task 13.7) can compose them
// inside in the same order the source rendered them (Header, Form, Action
// Buttons).
//
// Extracted verbatim from `AddEventForm.js` (the outer overlay <div>, the inner
// container <div>, and the "Header" block) as part of the behavior-preserving
// refactor (issue #240). The title text is "Add " followed by the type label,
// which the source derived from `getEventTypeLabel()` — here that label comes
// from the shared `getEventTypeLabel` pure logic (task 13.2), preserving the
// generic "Add Event" title for unknown/missing types. Render-only, wrapped in
// React.memo, with NO internal state — the close handler arrives via props.
// Styles, icons (lucide-react Plus/X), the inline hover handlers, and
// attributes are identical to the source.
//
// Props:
//   eventType - string; selects the title label via getEventTypeLabel
//   onCancel  - () => void invoked when the close "X" button is clicked
//   children  - the modal body content rendered after the header
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { X, Plus } from 'lucide-react';
import { getEventTypeLabel } from '../logic/getEventTypeLabel';

export const AddEventModalShell = React.memo(function AddEventModalShell({
  eventType,
  onCancel,
  children
}) {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Plus size={20} style={{ color: '#059669' }} />
            Add {getEventTypeLabel(eventType)}
          </h2>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '4px',
              borderRadius: '6px',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.color = '#6b7280'}
            onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
          >
            <X size={20} />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
});

export default AddEventModalShell;
